import { Box, Portal, useDisclosure } from '@chakra-ui/react';
import Footer from 'components/footer/Footer';
import { useBreakpointValue } from '@chakra-ui/react';
import Navbar from 'components/navbar/NavbarAdmin';
import Sidebar from 'components/sidebar/Sidebar';
import SidebarResponsive from 'components/sidebar/SidebarResponsive';
import { useEffect, useRef, useState } from 'react';
import { matchPath, Redirect, Route, Switch, useLocation } from 'react-router-dom';
import routes from 'routes';
import { SidebarContext } from 'utils/contexts/SidebarContext';
import { useKeycloak } from '@react-keycloak/web';
import { hasRole } from 'utils/auth-utils';
import { SIDEBAR_GUTTER, SIDEBAR_WIDTH_COLLAPSED, SIDEBAR_WIDTH_EXPANDED } from 'utils/constants';

export default function Dashboard(props: { [x: string]: any }) {
	const { keycloak } = useKeycloak();
	const location = useLocation();
	const { ...rest } = props;
	const [fixed] = useState(false);
	const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

	// True phone widths (below 'md') still get the hamburger + drawer — a persistent sidebar, even
	// collapsed to icons, eats too much of a phone screen. From 'md' up to 'xl' the sidebar stays on
	// screen but defaults to collapsed instead of swapping to a drawer.
	const isPhone = useBreakpointValue({ base: true, md: false });
	const contentOffset = isPhone ? 0 : (isSidebarCollapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH_EXPANDED) + SIDEBAR_GUTTER;

	// Below the 'xl' breakpoint (narrower desktop windows and tablets) the sidebar defaults to
	// collapsed instead of expanded — the user can still expand it via the sidebar's own toggle
	// button. Re-evaluated only when the breakpoint itself changes, so a manual expand isn't fought
	// as long as the window stays in the same width band.
	const isNarrowViewport = useBreakpointValue({ base: true, xl: false });
	useEffect(() => {
		if (isNarrowViewport !== undefined) {
			setIsSidebarCollapsed(isNarrowViewport);
		}
	}, [isNarrowViewport]);

	const currentLayout = `/${location.pathname.split('/')[1]}`;
	const isAdmin = hasRole(keycloak, 'admin') || hasRole(keycloak, 'ADMIN');

	const getRoute = () => {
		return window.location.pathname !== '/admin/full-screen-maps';
	};
	// A plain substring check (the old approach) never matches routes with a dynamic segment — e.g.
	// "/dipendente/rapporti-intervento/:id" never appears literally in "/dipendente/rapporti-intervento/9"
	// — so those pages silently fell back to "Home". matchPath resolves ":id" against the real segment,
	// the same way react-router itself matches routes for rendering.
	const getActiveRoute = (routes: RoutesType[]): string => {
		for (let i = 0; i < routes.length; i++) {
			if (matchPath(location.pathname, { path: routes[i].layout + routes[i].path, exact: true })) {
				return routes[i].name;
			}
		}
		return 'Home';
	};
	const getActiveNavbar = (routes: RoutesType[]): boolean => {
		for (let i = 0; i < routes.length; i++) {
			if (matchPath(location.pathname, { path: routes[i].layout + routes[i].path, exact: true })) {
				return routes[i].secondary;
			}
		}
		return false;
	};
	// Lets a route (e.g. a report page reached from the task list) override the breadcrumb's first
	// crumb, which otherwise always says "Home" — see RoutesType.breadcrumbParent.
	const getActiveRouteParent = (routes: RoutesType[]): { name: string; path: string } | null => {
		for (let i = 0; i < routes.length; i++) {
			if (matchPath(location.pathname, { path: routes[i].layout + routes[i].path, exact: true })) {
				return routes[i].breadcrumbParent ?? null;
			}
		}
		return null;
	};

	// The routed content lives in a scrollable Box (not the window), so react-router's default
	// behavior of leaving scroll position untouched on navigation means a page opened from partway
	// down a long list (e.g. a report opened from the tasks table) would render still scrolled down.
	const contentRef = useRef<HTMLDivElement>(null);
	useEffect(() => {
		contentRef.current?.scrollTo({ top: 0 });
	}, [location.pathname]);

	const getRoutes = (routes: RoutesType[]): any => {
		return routes.map((route: RoutesType, key: any) => {
			if (route.layout === currentLayout) {
				return <Route path={route.layout + route.path} component={route.component} key={key} />;
			}
			return null;
		});
	};
	document.documentElement.dir = 'ltr';
	const { onOpen } = useDisclosure();

	return (
		<Box>
			<SidebarContext.Provider
				value={{
					isCollapsed: isSidebarCollapsed,
					setIsCollapsed: setIsSidebarCollapsed
				}}>
				{isPhone ? (
					<SidebarResponsive routes={routes} />
				) : (
					<Sidebar routes={routes} />
				)}
				<Box
					ref={contentRef}
					float='right'
					minHeight='100vh'
					height='100%'
					overflow='auto'
					position='relative'
					maxHeight='100%'
					w={`calc(100% - ${contentOffset}px)`}
					maxWidth={`calc(100% - ${contentOffset}px)`}
					transition='all 0.33s cubic-bezier(0.685, 0.0473, 0.346, 1)'
					transitionDuration='.2s, .2s, .35s'
					transitionProperty='top, bottom, width'
					transitionTimingFunction='linear, linear, ease'>

					<Portal>
						<Box>
							<Navbar
								onOpen={onOpen}
								brandText={getActiveRoute(routes)}
								secondary={getActiveNavbar(routes)}
								breadcrumbParent={getActiveRouteParent(routes)}
								fixed={fixed}
								{...rest}
							/>
						</Box>
					</Portal>

					{getRoute() ? (
						<Box mx='auto' p={{ base: '20px', md: '0px' }} pe='20px' minH='100vh' pt={{ base: '70px', md: '30px' }}>
							<Switch>
								{getRoutes(routes)}
								<Redirect from='/' to={isAdmin ? '/admin/home' : '/dipendente/home'} />
							</Switch>
						</Box>
					) : null}
					<Box>
						<Footer />
					</Box>
				</Box>
			</SidebarContext.Provider>
		</Box>
	);
}
